import {
    Box,
    Button,
    CircularProgress,
    Dialog,
    DialogContent,
    DialogTitle,
    Fade,
    Grid,
    Rating,
    TextField,
    Typography
} from "@mui/material";
import {adminService} from "../../../api/services/admin";
import React, {useRef, useState} from "react";
import useSystemTheme from "../../../hooks/useSystemTheme";
import {useLocation, useParams} from "react-router-dom";
import useToast from "../../../hooks/useToast";
import html2canvas from "html2canvas";
import CarSection from "../CarSection";
import StatusTag from "./StatusTag";
import PaymentStatusTag from "../components/PaymentStatusTag";
import KeyTag from "../components/KeyTag";
import BackIcon from "../icons/BackIcon";
import {Formik} from "formik";
import * as Yup from "yup";
import {color} from "quill/ui/icons";
import StarBorderIcon from '@mui/icons-material/StarBorder';
import TicketDetails from "../components/TicketDetails";
import {staticImages} from "../../../assets/images";
import PoweredByNizek from "../icons/PoweredByNizek";
import Goto from "../icons/Goto";
import {BOOKING_STATUSES} from "../constant";
import EntranceRequestDialog from "src/components/EntranceRequestDialog";
import { getActiveEntrances } from "src/utils/entranceSelection";

const BaseStatusPage = ({details, onSubmitFeedback, onRequestCar}) => {
    const {toast} = useToast();
    const location = useLocation();
    const parameters = location.pathname.split("/");
    let hash = parameters[parameters.length - 1];
    const {isDarkMode} = useSystemTheme();
    const {token} = useParams();
    const [confirmModal, setConfirmModal] = useState(false);
    const [entranceModal, setEntranceModal] = useState(false);
    const [editExitModal, setEditExitModal] = useState(false);
    const reportTemplateRef = useRef(null);
    const [showTerms, setShowTerms] = useState(false);
    const [showFeedback, setShowFeedback] = useState(false);
    const [isLoading, setIsLoading] = useState(false);

    const textColor = isDarkMode ? "white" : "black";
    const backgroundColor = isDarkMode ? "black" : "white";
    const hasBranchEntrances = getActiveEntrances(details?.branchEntrances).length > 0;
    const isExitStatus = [
        BOOKING_STATUSES.REQUESTED,
        BOOKING_STATUSES.ASSIGNED,
        BOOKING_STATUSES.READY,
        BOOKING_STATUSES.DELIVERED,
    ].includes(details.BookingStatusName);
    const statusEntranceName = details.BookingStatusName === BOOKING_STATUSES.RE_PARKED
        ? null
        : (isExitStatus
            ? details.exitEntrance?.name
            : details.checkInEntrance?.name);
    const locationLabel = details.branchName
        ? (hasBranchEntrances && statusEntranceName
            ? `${details.branchName} - ${statusEntranceName}`
            : details.branchName)
        : '';
    const showRequestButton = Boolean(details.canRequest);
    const showEditExitButton = Boolean(
        details.canEditExitEntrance && getActiveEntrances(details?.branchEntrances).length >= 2,
    );
    const scrollPaddingBottom = showRequestButton && showEditExitButton
        ? 190
        : (showRequestButton || showEditExitButton ? 140 : 80);
    const shortScreenSpacing = (values) => (
        hasBranchEntrances ? { '@media (max-height: 780px)': values } : {}
    );
    const pageBackground = isDarkMode ? '#050406' : '#ffffff';

    const requestCar = async (payload = {}) => {
        try {
            setIsLoading(true);
            await adminService.requestCar(hash, {
                sameAsCheckIn: payload.sameAsCheckIn,
                exitEntranceId: payload.exitEntranceId,
            });
            onRequestCar && onRequestCar()
            setConfirmModal(false);
            setEntranceModal(false);
        } catch (error) {
            toast(error.response.data.message, "error");
            setIsLoading(false);
            if (error.response.data.message === 'this state in not valid for this booking.') {
                setTimeout(() => {
                    if (window) {
                        window.location.reload();
                    }
                }, 5000);
            }
        } finally {
            setIsLoading(false);
        }
    };

    const handleRequestClick = () => {
        const entrances = getActiveEntrances(details?.branchEntrances);
        if (entrances.length >= 2) {
            setEntranceModal(true);
            return;
        }
        setConfirmModal(true);
    };

    const handleEditExit = async (payload) => {
        try {
            setIsLoading(true);
            await adminService.updateClientExitEntrance(hash, payload);
            onRequestCar && onRequestCar();
            setEditExitModal(false);
        } catch (error) {
            toast(error.response.data.message, "error");
        } finally {
            setIsLoading(false);
        }
    };

    const downloadReceipt = () => {
        if (!reportTemplateRef.current) return;
        try {
            setIsLoading(true);
            html2canvas(reportTemplateRef.current, {
                backgroundColor: backgroundColor
            }).then((canvas) => {
                const a = document.createElement("a");
                a.href = canvas
                    .toDataURL("image/jpeg")
                    .replace("image/jpeg", "image/octet-stream");
                a.download = `Park_Receipt${details.checkedOut ? "_" + formatDate(details.checkedOut) : ""}.jpg`;
                a.click();
            });
        } finally {
            setIsLoading(false);
        }

    };

    function formatDate(isoString) {
        const date = new Date(isoString);

        const year = date.getFullYear();
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const day = String(date.getDate()).padStart(2, '0');

        return `${year}_${month}_${day}`;
    }

    const submitFeedback = async (
        formData,
        resetForm,
        setErrors,
        setStatus,
        setSubmitting
    ) => {
        formData["rating"] = +formData["rating"];
        try {
            setIsLoading(true);
            const response = await adminService.sendFeedback(token, formData);
            toast(response.data.message, "success");
            setSubmitting(false);
            resetForm();
            onSubmitFeedback && onSubmitFeedback()
            setStatus({success: true});
            setShowFeedback(false);
        } catch (err) {
            toast(err?.response?.data?.message || "Network Error!", "error");
            setSubmitting(false);
        } finally {
            setIsLoading(false);
        }
    };


    return (<>
        <>
            <Box
                sx={{
                    width: '100%',
                    height: '100vh',
                    minHeight: '100vh',
                    display: "flex",
                    flexDirection: "column",
                    position: "relative",
                    overflow: "hidden",
                    backgroundColor: pageBackground,
                }}
            >
                {/*Terms and Condition*/}
                <Fade in={showTerms} timeout={300}>
                    <Box
                        sx={{
                            position: "fixed",
                            top: 0,
                            width: '100%',
                            height: '100%',
                            left: '0',
                            zIndex: 1500,
                            backgroundColor: backgroundColor,
                            display: showTerms ? 'flex' : 'none',
                            flexDirection: "column",
                        }}
                    >
                        {/* Header */}
                        <Box
                            sx={{
                                width: "100%",
                                padding: "16px",
                                display: "flex",
                                alignItems: "center",
                                position: "relative",
                                backgroundColor: isDarkMode ? '#000000' : '#F9F9F9',
                            }}
                        >
                            <Button
                                onClick={() => setShowTerms(false)}
                                sx={{
                                    left: '0',
                                    margin: '0',
                                    padding: '0',
                                    justifyContent: 'flex-start',
                                }}
                            >
                                <BackIcon color={textColor}/>
                            </Button>
                            <Typography
                                color={textColor}
                                fontWeight={700}
                                fontSize={18}
                                lineHeight={1}
                                fontFamily="'Outfit', sans-serif"
                                sx={{
                                    position: 'absolute',
                                    left: '50%',
                                    transform: 'translateX(-50%)',
                                    whiteSpace: 'nowrap'
                                }}
                            >Terms and Conditions
                            </Typography>
                        </Box>

                        {/* Scrollable Content */}
                        <Box
                            sx={{
                                flex: 1,           // take all remaining height after header and button
                                overflowY: "auto",
                                padding: "20px",
                            }}>
                            <Typography
                                color={textColor}
                                fontWeight={400}
                                fontSize={14}
                                lineHeight={1}
                                fontFamily="'Outfit', sans-serif"
                                dangerouslySetInnerHTML={{__html: details.terms_and_conditions}}
                            />
                        </Box>

                        {/* Fixed Button at Bottom */}
                        <Box
                            sx={{
                                width: "100%",
                                padding: "32px 26px",
                                position: "sticky",
                                bottom: 0,
                                backgroundColor: isDarkMode ? '#1F1F1F' : '#FFFFFF',
                                display: "flex",
                                justifyContent: "center",
                            }}
                        >
                            <Button
                                variant="contained"
                                onClick={() => setShowTerms(false)}
                                sx={{
                                    height: '52px',
                                    width: "100%",
                                    backgroundColor: '#001489',
                                }}>
                                <Typography
                                    fontWeight={600}
                                    fontSize={16}
                                    lineHeight={1}
                                    fontFamily="'Outfit', sans-serif"
                                >Understood
                                </Typography>
                            </Button>
                        </Box>
                    </Box>
                </Fade>
                {/*Feedback*/}
                <Fade in={showFeedback} timeout={300}>
                    <Box
                        sx={{
                            position: "fixed",
                            top: 0,
                            width: '100%',
                            height: '100%',
                            left: '0',
                            zIndex: 1400,
                            backgroundColor: backgroundColor,
                            display: showFeedback ? 'flex' : 'none',
                            flexDirection: "column",
                            overflowY: "auto",
                        }}
                    >
                        {/* Header */}
                        <Box
                            sx={{
                                width: "100%",
                                padding: "16px",
                                display: "flex",
                                alignItems: "center",
                                position: "relative",
                                backgroundColor: isDarkMode ? '#000000' : '#F9F9F9',
                            }}
                        >
                            <Button
                                onClick={() => setShowFeedback(false)}
                                sx={{
                                    left: '0',
                                    margin: '0',
                                    padding: '0',
                                    justifyContent: 'flex-start',
                                }}
                            >
                                <BackIcon color={textColor}/>
                            </Button>
                            <Typography
                                color={textColor}
                                fontWeight={700}
                                fontSize={18}
                                lineHeight={1}
                                fontFamily="'Outfit', sans-serif"
                                sx={{
                                    position: 'absolute',
                                    left: '50%',
                                    transform: 'translateX(-50%)',
                                    whiteSpace: 'nowrap'
                                }}
                            >Feedback Us
                            </Typography>
                        </Box>
                        <Formik
                            sx={{
                                position: "relative",
                                height: "100%",
                                display: "flex",
                                flexDirection: "column",
                            }}
                            initialValues={{
                                rating: 0,
                                message: "",
                                subject: "",
                            }}
                            validationSchema={Yup.object().shape({
                                subject: Yup.string().required(
                                    "The subject field is required"
                                ),
                                message: Yup.string().required(
                                    "the message field is required"
                                ),
                            })}
                            onSubmit={async (
                                _values,
                                {resetForm, setErrors, setStatus, setSubmitting}
                            ) =>
                                console.log(_values) ||
                                submitFeedback(
                                    _values,
                                    resetForm,
                                    setErrors,
                                    setStatus,
                                    setSubmitting
                                )
                            }
                        >
                            {({
                                  errors,
                                  handleBlur,
                                  handleChange,
                                  handleSubmit,
                                  isSubmitting,
                                  touched,
                                  values,
                              }) => (
                                <form onSubmit={handleSubmit}>
                                    <Box
                                        sx={{
                                            height: '100%',
                                            flex: 1,
                                            padding: "20px",
                                        }}>
                                        <Grid container spacing={3} marginBottom={2}>
                                            <Grid item xs={12} md={12}>
                                                <Typography
                                                    marginTop={'24px'}
                                                    color={textColor}
                                                    fontWeight={500}
                                                    fontSize={14}
                                                    lineHeight={1}
                                                    fontFamily="'Outfit', sans-serif"
                                                >Please rate your experience below
                                                </Typography>
                                            </Grid>
                                            <Grid item xs={12}>
                                                <Rating
                                                    name="rating"
                                                    defaultValue={values.rating}
                                                    size="large"
                                                    onChange={handleChange}
                                                    onBlur={handleBlur}
                                                    emptyIcon={<StarBorderIcon
                                                        sx={{width: '30px', height: '30px', color: '#D1D5DB'}}/>}
                                                />
                                            </Grid>
                                            <Grid item xs={12} md={12}>
                                                <TextField
                                                    error={Boolean(
                                                        touched?.subject && errors.subject
                                                    )}
                                                    fullWidth
                                                    required
                                                    helperText={touched?.subject && errors.subject}
                                                    label={"Subject"}
                                                    name="subject"
                                                    onBlur={handleBlur}
                                                    onChange={handleChange}
                                                    value={values.subject}
                                                    variant="outlined"
                                                    fontFamily="'Outfit', sans-serif"
                                                    sx={{
                                                        '& .MuiOutlinedInput-root': {
                                                            '& fieldset': {
                                                                borderColor: '#D1D5DB',    // default border color
                                                            },
                                                            '&:hover fieldset': {
                                                                borderColor: '#A0AEC0',    // border color on hover (optional)
                                                            },
                                                            '&.Mui-focused fieldset': {
                                                                borderColor: '#D1D5DB',    // border color when focused
                                                            },
                                                        },
                                                        '& .MuiOutlinedInput-input': {
                                                            color: '#D1D5DB',           // text color inside input
                                                        },
                                                        '& .MuiInputLabel-root': {
                                                            color: '#D1D5DB',           // label color
                                                        },
                                                    }}
                                                />
                                            </Grid>
                                            <Grid item xs={12} md={12}>
                                                <TextField
                                                    error={Boolean(
                                                        touched?.message && errors.message
                                                    )}
                                                    fullWidth
                                                    required
                                                    helperText={touched?.message && errors.message}
                                                    label={"Message"}
                                                    name="message"
                                                    multiline
                                                    rows={4}
                                                    maxRows={5}
                                                    onBlur={handleBlur}
                                                    onChange={handleChange}
                                                    value={values.message}
                                                    variant="outlined"
                                                    fontFamily="'Outfit', sans-serif"
                                                    sx={{
                                                        '& .MuiOutlinedInput-root': {
                                                            '& fieldset': {
                                                                borderColor: '#D1D5DB',    // default border color
                                                            },
                                                            '&:hover fieldset': {
                                                                borderColor: '#A0AEC0',    // border color on hover (optional)
                                                            },
                                                            '&.Mui-focused fieldset': {
                                                                borderColor: '#D1D5DB',    // border color when focused
                                                            },
                                                        },
                                                        '& .MuiOutlinedInput-input': {
                                                            color: '#D1D5DB',           // text color inside input
                                                        },
                                                        '& .MuiInputLabel-root': {
                                                            color: '#D1D5DB',           // label color
                                                        },
                                                    }}
                                                />
                                            </Grid>
                                        </Grid>
                                    </Box>
                                    {/* Fixed Button at Bottom */}
                                    <Box
                                        sx={{
                                            width: "100%",
                                            padding: "32px 26px",
                                            position: 'fixed',
                                            bottom: 0,
                                            backgroundColor: isDarkMode ? '#1F1F1F' : '#FFFFFF',
                                            display: "flex",
                                            justifyContent: "center",
                                            alignItems: "space-between",
                                            gap: 1,
                                            zIndex: 10,
                                        }}
                                    >
                                        <Button
                                            variant="outlined"
                                            onClick={() => setShowFeedback(false)}
                                            sx={{
                                                height: '52px',
                                                width: "30%",
                                                color: isDarkMode ? '#001489' : 'black',
                                                border: '1.5px solid #EDEDED',
                                                background: 'white',
                                                "&:hover": {
                                                    backgroundColor: "#f3f4f6",
                                                    borderColor: "#4f46e5",
                                                    color: "#4f46e5",
                                                },
                                            }}>
                                            <Typography
                                                fontWeight={500}
                                                fontSize={'14px'}
                                                lineHeight={1}
                                                fontFamily="'Outfit', sans-serif"
                                            >Cancel
                                            </Typography>
                                        </Button>
                                        <Button
                                            type="submit"
                                            variant="contained"
                                            startIcon={isLoading || isSubmitting ?
                                                <CircularProgress size="1rem" sx={{color: 'white'}}/> : null}
                                            sx={{
                                                height: '52px',
                                                width: "70%",
                                                fontSize: '16px',
                                                fontWeight: 600,
                                                backgroundColor: '#001489',
                                            }}>
                                            <Typography
                                                fontWeight={600}
                                                fontSize={16}
                                                lineHeight={1}
                                                fontFamily="'Outfit', sans-serif"
                                            > Submit feedback
                                            </Typography>
                                        </Button>
                                    </Box>
                                </form>
                            )}
                        </Formik>
                    </Box>
                </Fade>
                {/*Receipt Details*/}
                <Fade in={details.BookingStatusName === BOOKING_STATUSES.DELIVERED} timeout={300}>
                    <Box
                        sx={{
                            position: "fixed",
                            top: 0,
                            width: '100%',
                            height: '100%',
                            left: '0',
                            zIndex: 1300,
                            backgroundColor: backgroundColor,
                            display: details.BookingStatusName === BOOKING_STATUSES.DELIVERED ? 'flex' : 'none',
                            flexDirection: "column",
                            overflowY: "auto",
                        }}
                    >
                        <Box
                            ref={reportTemplateRef}
                            sx={{
                                margin: '24px',
                                backgroundColor: '#8383833D',
                                borderColor: '#E2E2E2',
                                borderWidth: '1px',
                                borderStyle: 'solid',
                                borderRadius: '24px',
                            }}
                        >
                            <Box
                                display="flex"
                                flexDirection="row"
                                alignItems="center"
                                gap='8px'
                                padding='12px 24px'
                            >
                                <img style={{width: 40, height: 40}} src={staticImages.appLogo}/>
                                <Box display="flex" flexDirection="column" justifyContent="center">
                                    <Typography
                                        color={isDarkMode ? "#E2E2E2" : "#A9A9A9"}
                                        fontWeight={700}
                                        fontSize={16}
                                        lineHeight='30px'
                                        fontFamily="'Outfit', sans-serif"
                                    >Receipt Details
                                    </Typography>
                                    <Typography
                                        color={isDarkMode ? "#FFFFFF" : "#000000"}
                                        fontWeight={400}
                                        fontSize={12}
                                        lineHeight={1}
                                        fontFamily="'Outfit', sans-serif"
                                    >Thank you for parking with us
                                    </Typography>
                                </Box>
                            </Box>
                            <TicketDetails details={details}></TicketDetails>
                        </Box>
                        {/*<Box display="flex" justifyContent="center" marginTop="16px" width="100%">*/}
                        {/*    <SupportTag/>*/}
                        {/*</Box>*/}
                        <Box
                            display="flex"
                            alignItems="center"
                            justifyContent="space-between"
                            padding="24px 24px"
                            gap={'12px'}
                            width="100%"
                        >
                            <Button
                                variant="contained"
                                onClick={() => downloadReceipt()}
                                startIcon={isLoading ? <CircularProgress size="1rem" sx={{color: textColor}}/> : null}
                                sx={{
                                    height: '52px',
                                    width: "50%",
                                    backgroundColor: '#001489',
                                }}
                            > <Typography
                                    fontWeight={600}
                                fontSize={16}
                                lineHeight={1}
                                fontFamily="'Outfit', sans-serif"
                            >Download
                            </Typography>
                            </Button>
                            <Button
                                variant="contained"
                                onClick={() => setShowFeedback(true)}
                                sx={{
                                    height: '52px',
                                    width: "50%",
                                    backgroundColor: '#001489',
                                }}
                            > <Typography
                                fontWeight={600}
                                fontSize={16}
                                lineHeight={1}
                                fontFamily="'Outfit', sans-serif"
                            >Feedback
                            </Typography>
                            </Button>
                        </Box>
                        <Button
                            onClick={() => setShowTerms(!showTerms)}
                            sx={{
                                height: '52px',
                                width: "100%",
                                marginTop: '8px',
                                padding: '8px'
                            }}>
                            <Typography
                                color={textColor}
                                fontWeight={500}
                                fontSize={14}
                                fontFamily="'Outfit', sans-serif"
                            >Terms and Conditions
                            </Typography>
                        </Button>
                        <Box
                            onClick={() => window.open("https://www.nizek.com", "_blank")}
                            sx={{
                                width: "100%",
                                padding: "24px 14px",
                                display: "flex",
                                flexDirection: "row",
                                justifyContent: "space-between",
                                borderTop: `1px solid ${isDarkMode ? '#424242' : '#EAEAEA'}`,
                                cursor: "pointer",
                            }}
                        >
                            <PoweredByNizek/>
                            <Goto/>
                        </Box>
                    </Box>
                </Fade>
                {/*Main box*/}
                <Box
                    sx={{
                        flex: 1,
                        minHeight: 0,
                        overflowY: "auto",
                        paddingBottom: `${scrollPaddingBottom}px`,
                    }}
                >
                    <Box
                        display={'flex'}
                        justifyContent={'center'}
                        alignItems={'center'}>
                        <CarSection
                            status={details.BookingStatusName}
                            isDarkMode={isDarkMode}
                            compactOnShortScreen={hasBranchEntrances}
                        />
                    </Box>
                    {locationLabel ? (
                        <Typography
                            sx={{
                                marginTop: '-40px',
                                ...shortScreenSpacing({ marginTop: '-32px' }),
                            }}
                            color={isDarkMode ? "#FFFFFF" : "#001489"}
                            fontWeight={600}
                            fontSize={24}
                            textAlign={'center'}
                            fontFamily="'Outfit', sans-serif"
                        >{locationLabel}
                        </Typography>
                    ) : null}
                    <Box
                        sx={{
                            marginTop: '32px',
                            ...shortScreenSpacing({ marginTop: '22px' }),
                        }}
                        display="flex"
                        alignItems="center"
                        justifyContent="space-between"
                        width="100%"
                    >
                        <Typography
                            color={textColor}
                            fontWeight={400}
                            fontSize={16}
                            fontFamily="'Outfit', sans-serif"
                        >Status:
                        </Typography>
                        <StatusTag
                            status={details.BookingStatusName}
                            isDarkMode={isDarkMode}
                        />
                    </Box>
                    <Typography
                        sx={{
                            marginTop: '20px',
                            ...shortScreenSpacing({ marginTop: '14px' }),
                        }}
                        color={textColor}
                        fontWeight={400}
                        fontSize={16}
                        fontFamily="'Outfit', sans-serif"
                        dangerouslySetInnerHTML={{__html: details.text}}
                    />
                    <Box
                        sx={{
                            marginTop: '20px',
                            ...shortScreenSpacing({ marginTop: '14px' }),
                        }}
                        display="flex"
                        alignItems="center"
                        justifyContent="space-between"
                        width="100%"
                    >
                        <KeyTag keytag={details.keytag}/>
                        <PaymentStatusTag paymentMethod={details.paymentMethod}/>
                    </Box>
                    {hasBranchEntrances ? (
                        <Box
                            sx={{
                                marginTop: '20px',
                                gap: '12px',
                                ...shortScreenSpacing({
                                    marginTop: '14px',
                                    gap: '8px',
                                }),
                            }}
                            display="flex"
                            flexDirection="column"
                            width="100%"
                        >
                            {[
                                { label: 'Check-in Entrance', value: details.checkInEntrance?.name, highlight: false },
                                {
                                    label: 'Exit Entrance',
                                    value: details.exitEntrance?.name,
                                    highlight: Boolean(
                                        details.exitDiffersFromCheckIn
                                        ?? (
                                            details.checkInEntrance?.id
                                            && details.exitEntrance?.id
                                            && details.checkInEntrance.id !== details.exitEntrance.id
                                        ),
                                    ),
                                },
                            ].map((row) => (
                                <Box
                                    key={row.label}
                                    display="flex"
                                    alignItems="flex-start"
                                    justifyContent="space-between"
                                    gap="16px"
                                    width="100%"
                                    sx={row.highlight ? {
                                        boxSizing: 'border-box',
                                        borderRadius: '12px',
                                        padding: '12px 0',
                                        ...shortScreenSpacing({ padding: '10px 0' }),
                                        backgroundColor: isDarkMode ? 'rgba(169, 181, 255, 0.12)' : '#EEF2FF',
                                        border: isDarkMode
                                            ? '1px solid rgba(169, 181, 255, 0.35)'
                                            : '1px solid #C5D0FF',
                                    } : undefined}
                                >
                                    <Typography
                                        color={isDarkMode ? "#A9A9A9" : "#878787"}
                                        fontWeight={400}
                                        fontSize={14}
                                        fontFamily="'Outfit', sans-serif"
                                        sx={row.highlight ? { marginLeft: '-1px' } : undefined}
                                    >
                                        {row.label}
                                    </Typography>
                                    <Typography
                                        color={row.highlight
                                            ? (isDarkMode ? '#A9B5FF' : '#001489')
                                            : textColor}
                                        fontWeight={600}
                                        fontSize={16}
                                        textAlign="right"
                                        fontFamily="'Outfit', sans-serif"
                                        sx={row.highlight ? { marginRight: '-1px' } : undefined}
                                    >
                                        {row.value || '—'}
                                    </Typography>
                                </Box>
                            ))}
                        </Box>
                    ) : null}
                </Box>
                <Box
                    sx={{
                        width: '100%',
                        maxWidth: '600px',
                        display: "flex",
                        justifyContent: "center",
                        alignItems: "center",
                        flexDirection: "column",
                        position: 'fixed',
                        bottom: 0,
                        left: '50%',
                        transform: 'translateX(-50%)',
                        backgroundColor: pageBackground,
                        zIndex: 100,
                        padding: '20px',
                        boxSizing: 'border-box',
                    }}>
                    {
                        showRequestButton && (
                            <Button
                                variant="outlined"
                                onClick={handleRequestClick}
                                sx={{
                                    width: "100%",
                                    height: "52px",
                                    backgroundColor: "#FFFFFF",
                                    color: "#4f46e5",
                                    borderColor: "#4f46e5",
                                    "&:hover": {
                                        backgroundColor: "#f3f4f6",
                                        borderColor: "#4f46e5",
                                        color: "#4f46e5",
                                    },
                                }}
                            >
                                <Typography
                                    color={isDarkMode ? undefined : '#001489'}
                                    fontWeight={600}
                                    fontSize={16}
                                    lineHeight={1}
                                    fontFamily="'Outfit', sans-serif"
                                >Request Your Car
                                </Typography>
                            </Button>
                        )
                    }
                    {
                        showEditExitButton && (
                            <Button
                                variant="outlined"
                                onClick={() => setEditExitModal(true)}
                                sx={{
                                    width: "100%",
                                    height: "52px",
                                    marginTop: showRequestButton ? '8px' : 0,
                                    backgroundColor: "#FFFFFF",
                                    color: "#4f46e5",
                                    borderColor: "#4f46e5",
                                }}
                            >
                                <Typography
                                    fontWeight={600}
                                    fontSize={16}
                                    lineHeight={1}
                                    fontFamily="'Outfit', sans-serif"
                                >Update Exit Entrance
                                </Typography>
                            </Button>
                        )
                    }
                    <Button
                        onClick={() => setShowTerms(!showTerms)}
                        sx={{
                            marginTop: '4px',
                            padding: '0',
                            minHeight: 0,
                            minWidth: 0,
                        }}>
                        <Typography
                            color={textColor}
                            fontWeight={500}
                            fontSize={14}
                            fontFamily="'Outfit', sans-serif"
                            sx={{
                                textDecoration: 'underline',
                            }}
                        >Terms and Conditions
                        </Typography>
                    </Button>
                </Box>
            </Box>
            <Dialog
                open={confirmModal}
                onClose={() => setConfirmModal(false)}
                BackdropProps={{
                    sx: {
                        backgroundColor: '#111111CC'
                    }
                }}
                PaperProps={{
                    sx: {
                        backgroundColor: isDarkMode ? '#2D2D2D' : '#FFFFFF',
                        position: 'fixed',
                        borderRadius: '16px',
                        padding: '24px 16px',
                        fontFamily: 'Outfit, sans-serif',
                        fontWeight: 500,
                        bottom: '0',
                        left: '20px',
                        right: '20px',
                        maxWidth: '400px',
                        margin: '20px auto',
                        boxShadow: 'none'
                    }
                }}
            >
                <DialogTitle
                    sx={{
                        color: textColor,
                        textAlign: 'center',
                        padding: '0 0 24px 0',
                        fontSize: '16px',
                        position: 'relative',
                        '&::after': {
                            content: '""',
                            position: 'absolute',
                            bottom: 0,
                            left: '50%',
                            transform: 'translateX(-50%)',
                            width: '100%',
                            height: '1px',
                            background: isDarkMode
                                ? 'radial-gradient(circle at center, #DADADA 0%, #747474 100%)'
                                : 'radial-gradient(circle at center, #DADADA 0%, #E0E0E0 100%)',
                        }
                    }}
                >
                    <Typography
                        fontWeight={500}
                        fontSize={16}
                        lineHeight={1}
                        fontFamily="'Outfit', sans-serif"
                    >Request Your Car
                    </Typography>
                </DialogTitle>
                <DialogContent sx={{
                    padding: '0',
                }}>
                    <Typography
                        fontFamily="'Outfit', sans-serif"
                        padding={'16px 0 24px 0'}
                        fontSize={'14px'}
                        fontWeight={500}
                        color={textColor}
                        lineHeight={1}
                    >Are you sure you want to request your car?</Typography>
                    <Box
                        display="flex"
                        justifyContent="cenetr"
                        marginTop={2}
                    >
                        <Button
                            onClick={() => setConfirmModal(false)}
                            variant="outlined"
                            sx={{
                                height: '52px',
                                width: "50%",
                                borderRadius: '8px',
                                px: 4,
                                py: 1.5,
                                fontWeight: 600,
                                color: textColor,
                                borderColor: textColor,
                            }}
                        >
                            <Typography
                                fontWeight={400}
                                fontSize={14}
                                lineHeight={1}
                                fontFamily="'Outfit', sans-serif"
                            >Cancel
                            </Typography>
                        </Button>
                        <Button
                            variant="contained"
                            startIcon={isLoading ? <CircularProgress size="1rem" sx={{color: 'white'}}/> : null}
                            onClick={requestCar}
                            sx={{
                                width: "50%",
                                marginLeft: 1,
                                borderRadius: '8px',
                                px: 4,
                                py: 1.5,
                                fontWeight: 600,
                                backgroundColor: '#001489',
                                color: "white",
                                height: '52px'
                            }}
                        >
                            <Typography
                                fontWeight={600}
                                fontSize={14}
                                lineHeight={1}
                                fontFamily="'Outfit', sans-serif"
                            >Yes
                            </Typography>
                        </Button>
                    </Box>
                </DialogContent>
            </Dialog>
            <EntranceRequestDialog
                open={entranceModal}
                onClose={() => setEntranceModal(false)}
                onConfirm={requestCar}
                booking={{
                    ...details,
                    checkInEntrance: details?.checkInEntrance,
                    checkInEntranceId: details?.checkInEntrance?.id,
                    branchEntrances: details?.branchEntrances,
                }}
                title="Request Your Car"
                confirmLabel="Request Your Car"
                loading={isLoading}
            />
            <EntranceRequestDialog
                open={editExitModal}
                onClose={() => setEditExitModal(false)}
                onConfirm={handleEditExit}
                booking={{
                    ...details,
                    checkInEntrance: details?.checkInEntrance,
                    checkInEntranceId: details?.checkInEntrance?.id,
                    branchEntrances: details?.branchEntrances,
                }}
                title="Update Exit Entrance"
                confirmLabel="Save"
                loading={isLoading}
                mode="update"
            />
        </>
    </>)
}

export default BaseStatusPage